/**
 * Converts response data to CSV format
 * @param {Array} data - The responses data array
 * @returns {string} CSV formatted string
 */
export const convertResponsesToCSV = (data) => {
  const headers = ['Sitzung', 'Aufgabe', 'Antwort-1', 'Antwort-2', 'Zeit (ms)'];
  
  const rows = data.map(item => {
    let antwort1 = item.item_resp;
    let antwort2 = '';
    
    if (item.item_resp?.includes(';')) {
      const [first, second] = item.item_resp.split(';');
      antwort1 = second || '';
      antwort2 = first;
    }
    
    // Escape values and wrap in quotes to handle commas
    return [
      escapeCSVValue(item.session_id),
      escapeCSVValue(item.item_name),
      escapeCSVValue(antwort1),
      escapeCSVValue(antwort2),
      escapeCSVValue(item.resp_time)
    ].join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

/**
 * Escapes a value for CSV formatting
 * @param {string} value - The value to escape
 * @returns {string} Properly escaped CSV value
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) return '""';
  const stringValue = String(value);
  // Escape quotes by doubling them
  const escapedValue = stringValue.replace(/"/g, '""');
  // Wrap in quotes if contains commas, newlines, or quotes
  return /[,"\n]/.test(escapedValue) ? `"${escapedValue}"` : escapedValue;
};

/**
 * Triggers download of a CSV file
 * @param {string} csvContent - The CSV content string
 * @param {string} fileName - The desired filename
 */
export const downloadCSV = (csvContent, fileName) => {
  // Add UTF-8 BOM for better Excel compatibility
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Gets current date-time in format YYYY_MM_DD_HH_mm
 * @returns {string} Formatted datetime string
 */
const getCurrentDateTimeString = () => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0')
  ].join('_');
};


/**
 * Combined function to convert and download responses as CSV
 * @param {Array} responses - The responses data array
 * @param {Array} excludedItems - Items to exclude
 * @param {string} participantCode - Participant code to include in filename
 */
export const downloadResponsesCSV = (
  responses, 
  excludedItems = [], 
  participantCode = ''
) => {
  const filteredResponses = responses.filter(r => !excludedItems.includes(r.item_name));
  const csvContent = convertResponsesToCSV(filteredResponses);
  
  // Generate filename
  const codePart = participantCode ? `${participantCode}_` : '';
  const datetimePart = getCurrentDateTimeString();
  const fileName = `responses_${codePart}${datetimePart}.csv`;
  
  downloadCSV(csvContent, fileName);
};